/**
 * Thin fetch wrapper for the BFF.
 *
 * `credentials: 'same-origin'` is what carries the session cookie, replacing the
 * ambient cookie the Razor pages relied on. A 401 means the session expired —
 * the ASP.NET cookie middleware used to answer that with a 302 to
 * /Account/Login, so the same redirect happens here, just from the client.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** The PascalCase envelope every upstream-backed endpoint returns. */
export interface Response<T> {
  Success: boolean
  Data: T | null
  Message: string | null
}

export interface PagedResponse<T> extends Response<T[]> {
  Count: number
  PageNumber: number
  PageSize: number
}

/** The lowercase envelope the Account endpoints return, matching AccountController. */
export interface ActionResult {
  success: boolean
  message?: string | null
  redirectUrl?: string
  redirectTo?: string
  requiresVerification?: boolean
  requiresProfileCompletion?: boolean
  [key: string]: unknown
}

function loginRedirect(): never {
  // Preserve where the user was, the way the cookie middleware's ReturnUrl did.
  const returnUrl = encodeURIComponent(window.location.pathname + window.location.search)
  window.location.assign(`/Account/Login?ReturnUrl=${returnUrl}`)
  throw new ApiError('Unauthorized', 401)
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.headers ?? {}),
    },
  })

  if (res.status === 401) loginRedirect()

  const text = await res.text()
  const body: unknown = text ? safeJson(text) : null

  if (!res.ok) {
    throw new ApiError(extractMessage(body) ?? `Request failed with ${res.status}`, res.status, body)
  }
  return body as T
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

function extractMessage(body: unknown): string | null {
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>

    /**
     * profile/saveAll answers a partial failure with `{ Message, Errors: [...] }`
     * where Errors names the sections that did not save. Without them the user
     * only ever sees "Some sections failed to save", which says nothing about
     * what to fix — the exact dead end the .NET original left people in.
     */
    const errors = record['Errors']
    if (Array.isArray(errors) && errors.length > 0) {
      const detail = errors.filter((item): item is string => typeof item === 'string')
      if (detail.length > 0) return detail.join('\n')
    }

    for (const key of ['Message', 'message', 'error']) {
      const value = record[key]
      if (typeof value === 'string') return value
    }
  }
  return null
}

export const api = {
  get: <T>(path: string) => request<T>(path),

  /**
   * The Content-Type header is only sent when there IS a body.
   *
   * Declaring `application/json` on a bodyless POST makes Fastify reject the
   * request with 400 "Body cannot be empty when content-type is set to
   * 'application/json'" — which is what the session-scoped endpoints that take
   * no arguments (joinedTalent, talentCommunity) were hitting.
   */
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      ...(body === undefined
        ? {}
        : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    }),

  del: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'DELETE',
      ...(body === undefined
        ? {}
        : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    }),

  /**
   * Multipart post. The profile and apply forms send JSON blobs inside form
   * fields alongside the file parts — the shape the .NET controllers read as
   * IFormCollection — so FormData is built by the caller and passed through.
   * Content-Type is deliberately not set: the browser adds the boundary.
   */
  form: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),
}
